import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { CoverageLevel, SkinTone } from '@seddi/ui-babylon-fitroom';
import { SessionData } from '../../session';
import { HomeState, HomeStyle } from '../store/homeState';

export type SetAvatarDisplayPayload = {
  avatarDisplayCovBottom?: CoverageLevel;
  avatarDisplayCovTop?: CoverageLevel;
  avatarDisplaySkin?: SkinTone;
};

const initialState: HomeState = {
  avatarDisplayCovBottom: CoverageLevel.MODERATE,
  avatarDisplayCovTop: CoverageLevel.MODERATE,
  avatarDisplaySkin: SkinTone.BROWN_3,
  avatarId: null,
  avatarWaitMessage: undefined,
  garmentId: null,
  isAvatarBuilding: false,
  isFitBuilding: false,
  isHeatMap: false,
  isSizeRecommendationWaiting: false,
  poseId: null,
  style: null,
};

/**
 * Home slice.
 */
export const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    beginAvatarBuild(state: HomeState) {
      state.isAvatarBuilding = true;
    },
    beginFit(state: HomeState) {
      state.isFitBuilding = true;
    },
    beginSizeRecommendation(state: HomeState) {
      state.isSizeRecommendationWaiting = true;
    },
    endAvatarBuild(state: HomeState) {
      state.isAvatarBuilding = false;
    },
    endFit(state: HomeState) {
      state.isFitBuilding = false;
    },
    endSizeRecommendation(state: HomeState) {
      state.isSizeRecommendationWaiting = false;
    },
    setAvatar: (state: HomeState, action: PayloadAction<string>) => {
      state.avatarId = action.payload;
    },
    setAvatarDisplay: (state: HomeState, action: PayloadAction<SetAvatarDisplayPayload>) => {
      const { avatarDisplayCovBottom, avatarDisplaySkin, avatarDisplayCovTop } = action.payload;

      if (avatarDisplayCovBottom) {
        state.avatarDisplayCovBottom = avatarDisplayCovBottom;
      }
      if (avatarDisplaySkin) {
        state.avatarDisplaySkin = avatarDisplaySkin;
      }
      if (avatarDisplayCovTop) {
        state.avatarDisplayCovTop = avatarDisplayCovTop;
      }
    },
    setAvatarWaitMessage(state: HomeState, action: PayloadAction<HomeState['avatarWaitMessage']>) {
      state.avatarWaitMessage = action.payload;
    },
    setGarment: (state: HomeState, action: PayloadAction<string>) => {
      state.garmentId = action.payload;
    },
    setPose: (state: HomeState, action: PayloadAction<string>) => {
      state.poseId = action.payload;
    },
    setSession: (state: HomeState, action: PayloadAction<SessionData>) => {
      const { avatarDisplayCovBottom, avatarDisplaySkin, avatarDisplayCovTop, avatarId, poseId } =
        action.payload;

      if (avatarDisplayCovBottom) {
        state.avatarDisplayCovBottom = avatarDisplayCovBottom;
      }
      if (avatarDisplaySkin) {
        state.avatarDisplaySkin = avatarDisplaySkin;
      }
      if (avatarDisplayCovTop) {
        state.avatarDisplayCovTop = avatarDisplayCovTop;
      }
      if (avatarId) {
        state.avatarId = avatarId;
      }
      if (poseId) {
        state.poseId = poseId;
      }
    },
    setStyleVariant: (state: HomeState, action: PayloadAction<HomeStyle>) => {
      state.garmentId = null;
      state.style = action.payload;
    },
    toggleHeatMap: (state: HomeState) => {
      state.isHeatMap = !state.isHeatMap;
    },
    unsetAvatar: (state: HomeState) => {
      state.avatarId = null;
    },
  },
});

export const homeActions = homeSlice.actions;

/**
 * Home state selector.
 * @param state the root state.
 * @returns the home state.
 */
// export const homeSelector = (state: HomeState): HomeState["home"] => state.home;

/**
 * Home avatar state selector.
 * @param state the root state.
 * @returns the home avatar state.
 */
// export const homeAvatarSelector = (state: HomeState): string | null =>
//   state.avatarId;

/**
 * Home avatarHistory state selector.
 * @param state the root state.
 * @returns the home avatarHistory state.
 */
// export const homeAvatarHistorySelector = (state: HomeState): string[] =>
//   state.home.avatarHistory;

/**
 * Home garment state selector.
 * @param state the root state.
 * @returns the home avatar state.
 */
// export const homeGarmentSelector = (state: HomeState): string =>
//   state.home.styleId;

/**
 * Home isFitBuilding state selector.
 * @param state the root state.
 * @returns the home isFitBuilding state.
 */
// export const homeIsFitLoadingSelector = (state: HomeState): boolean =>
//   state.home.isFitBuilding;

/**
 * Home isSizeRecommendationWaiting state selector.
 * @param state the root state.
 * @returns the home isSizeRecommendationWaiting state.
 */
// export const homeIsSizeRecommendationLoadingSelector = (
//   state: HomeState
// ): boolean => state.home.isSizeRecommendationWaiting;

/**
 * Home pose state selector.
 * @param state the root state.
 * @returns the home avatar pose state.
 */
// export const homePoseSelector = (state: HomeState): string => state.home.poseId;

/**
 * Home scene state selector.
 * @param state the root state.
 * @returns the home scene state.
 */
// export const homeSceneSelector = (state: HomeState): HomeSceneState =>
//   state.home.scene;

/**
 * org id state selector.
 * @param state the root state.
 * @returns the orgId state.
 */
// export const orgIdSelector = (state: HomeState): string => state.home.orgId;

/**
 * Home reducer.
 */
export const homeReducer = homeSlice.reducer;
